<template>

    <v-form autocomplete="off" class="fill-height">
        <v-card class="fill-height d-flex flex-column">
            <v-toolbar dark color="primary" flat style="max-height: 64px;">
                <v-toolbar-title>Receive EDN</v-toolbar-title>
                <slot name="header"></slot>
            </v-toolbar>

            <v-card-text class="flex-grow-1">
                <v-container grid-list-md v-if="sources">
                    <v-flex xs12 v-if="hasWarranty">
                        <v-menu v-model="showWarranties" offset-y class="pa-1">
                            <template v-slot:activator="{ on }">
                                <v-card v-on="on">
                                    <v-alert dense border="left" type="warning">
                                        This unit may be covered under warranty
                                        <span class="overline" v-if="showWarranties">(hide&nbsp;</span>
                                        <span class="overline" v-else>(show&nbsp;</span>
                                        <v-icon v-if="showWarranties">arrow_drop_up</v-icon>
                                        <v-icon v-else>arrow_drop_down</v-icon>
                                        <span class="overline">)</span>
                                    </v-alert>
                                </v-card>
                            </template>
                            <v-card class="pa-3">
                                <unit-warranty-fields :unit="warranty"></unit-warranty-fields>
                                <unit-warranty-list disabled :items="customWarranties" />
                            </v-card>
                        </v-menu>
                    </v-flex>
                    <v-layout wrap>
                        <v-flex xs6>
                            <v-text-field v-model="value.ednNumber" label="EDN Number" placeholder v-validate="'required'" :error-messages="errors.first('edn')" name="edn" maxlength="30" required></v-text-field>
                        </v-flex>
                        <v-flex xs6>
                            <v-text-field v-validate="roadsideIntercept" v-model="value.incidentNumber" label="Incident/Reference #" placeholder :error-messages="errors.first('reference')" name="reference" maxlength="30"></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                            <v-autocomplete
                                label="Unit"
                                item-value="id"
                                item-text="number"
                                :items="units"
                                no-filter
                                :loading="isSearchingUnit"
                                :search-input.sync="search"
                                v-model="selectedUnit"
                                v-validate="'required'"
                                no-data-text="Start typing a Unit Name or Number"
                                :error-messages="errors.collect('unit')"
                                return-object
                                name="unit"
                                required
                                autocomplete="off"
                            >
                                <template v-slot:item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.number }} ({{ item.garage }})</v-list-item-title>
                                        <v-list-item-subtitle v-text="item.name"></v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-flex>
                        <v-flex xs6>
                            <v-menu v-model="receivedDateDialog" :close-on-content-click="false" max-width="290">
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="formattedReceivedDate"
                                        label="Date Received"
                                        readonly
                                        v-validate="'required'"
                                        :error-messages="errors.first('received date')"
                                        name="received date"
                                        v-on="on"
                                        required
                                        append-icon="event"
                                        @click:append="receivedDateDialog = true"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    :min="minDate"
                                    :max="maxDate"
                                    no-title
                                    v-model="value.receivedDate"
                                    @change="
                                        receivedDateDialog = false;
                                        setFormattedDates();
                                    "
                                ></v-date-picker>
                            </v-menu>
                        </v-flex>
                        <v-flex xs6>
                            <v-select v-model="selectedSource" return-object item-text="name" :items="sources"></v-select>
                        </v-flex>

                        <v-flex xs6>
                            <v-text-field
                                v-model.number="value.receivedTotal"
                                label="Defects Received"
                                placeholder="e.g. 1"
                                v-validate="'required|min_value:1|max_value:6'"
                                :error-messages="errors.collect('received')"
                                name="received"
                                type="number"
                                min="1"
                                max="6"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs6>
                            <v-checkbox v-model="actionNow" label="Action EDN Now"></v-checkbox>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <slot name="actions"></slot>
                <v-btn color="primary" :disabled="disableSubmit" @click="submit">{{ !this.editing ? 'Receive EDN' : 'Update' }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>
import moment from 'moment';
import { Validator } from 'vee-validate';
import FormValidation from '@/components/FormValidation.vue';
import { DefectSourcesStore } from '@/stores/DefectSourcesStore';
import { GarageLocationStore } from '@/stores/GarageLocationStore';
import UnitWarrantyFields from '@/components/Units/Details/UnitWarrantyFields.vue';
import UnitWarrantyList from '@/components/Units/Details/UnitWarrantyList.vue';
import { DateFormat } from '@/plugins/date-filter';
import * as _ from 'lodash';

export default {
    props: {
        value: {
            required: true
        }
    },
    components: { UnitWarrantyFields, UnitWarrantyList },
    mixins: [FormValidation],
    computed: {
        minDate() {
            return moment('1900-01-01').format(DateFormat.Canonical);
        },
        maxDate() {
            return moment().format(DateFormat.Canonical);
        },
        roadsideIntercept() {
            return this.selectedSource && this.selectedSource.name === 'Roadside Intercept' ? 'required' : '';
        }
    },
    methods: {
        async init() {
            this.submitted = false;
            this.actionNow = false;
            this.hasWarranty = false;
            this.showWarranties = false;
            this.sources = await DefectSourcesStore.methods.getData();
            this.editing = !!this.value.id;
            if (!this.editing) {
                this.value.id = this.$uuid.v1();

                this.value.source = this.sources[0];
                this.selectedSource = this.sources[0];
                this.value.sourceId = this.selectedSource.id;

                this.selectedUnit = null;
                this.search = null;

                this.value.garageLocationId = GarageLocationStore.data.userGarage;

                this.value.receivedDate = moment().format(DateFormat.Canonical);
                this.setFormattedDates();
            }

            this.id = this.value.id;
        },
        submitForm() {
            this.submitted = true;
            const unitNumber = unitNumber;
            this.$emit('receive', { id: this.id, model: { ...this.value, unitNumber, sourceSystem: 'GateKeeper' }, actionNow: this.actionNow });
            setTimeout(() => (this.submitted = false), 1000);
        },
        setFormattedDates() {
            this.formattedReceivedDate = this.value.receivedDate ? moment(this.value.receivedDate).format(DateFormat.DefaultDisplay) : '';
        },
        showWarrantiesMenu(e) {
            e.preventDefault();
            this.showWarranties = false;
            this.$nextTick(() => {
                this.showWarranties = true;
            });
        },
        performSearch: _.debounce(async (val, me) => {
            if (!me.search) {
                me.search = me.selectedUnit ? me.selectedUnit.name : '';
            }
            if (!me.search) {
                return;
            }

            try {
                me.isSearchingUnit = true;
                const response = await me.$api.get(`units?searchTerm=${me.search}&unitType='both'&orderByUnitMatch=true`);
                me.units = response.data.data;
            } catch {
                me.$toast.error('There was an error getting units');
            }
            me.isSearchingUnit = false;
        }, 200)
    },
    data() {
        return {
            id: '',
            submitted: false,
            units: [],
            isSearchingUnit: false,
            search: null,
            selectedUnit: null,
            selectedSource: null,
            sources: null,
            receivedDateDialog: false.selectedUnit,
            editing: false,
            formattedReceivedDate: null,
            actionNow: false,
            hasWarranty: false,
            showWarranties: false,
            warranty: {
                bumperToBumperWarrantyStart: null,
                bumperToBumperWarrantyEnd: null,
                engineWarrantyStart: null,
                engineWarrantyEnd: null,
                transmissionWarrantyStart: null,
                transmissionWarrantyEnd: null,
                bodyWarrantyStart: null,
                bodyWarrantyEnd: null
            },
            customWarranties: []
        };
    },
    watch: {
        async selectedUnit() {
            this.hasWarranty = false;
            if (!this.selectedUnit) {
                this.value.unitId = null;
                this.value.unitNumber = null;
                return;
            }

            this.value.unitId = this.selectedUnit.id;
            this.value.unitNumber = this.selectedUnit.number;

            try {
                const response = await this.$api.get(`units/${this.value.unitId}`);
                this.hasWarranty = response.data.isUnderWarranty || response.data.isUnderCustomWarranty;
                if (this.hasWarranty) {
                    this.warranty.bumperToBumperWarrantyStart = response.data.bumperToBumperWarrantyStart;
                    this.warranty.bumperToBumperWarrantyEnd = response.data.bumperToBumperWarrantyEnd;
                    this.warranty.engineWarrantyStart = response.data.engineWarrantyStart;
                    this.warranty.engineWarrantyEnd = response.data.engineWarrantyEnd;
                    this.warranty.transmissionWarrantyStart = response.data.transmissionWarrantyStart;
                    this.warranty.transmissionWarrantyEnd = response.data.transmissionWarrantyEnd;
                    this.warranty.bodyWarrantyStart = response.data.bodyWarrantlyStart;
                    this.warranty.bodyWarrantyEnd = response.data.bodyWarrantlyEnd;
                    this.warranty.subjectToCampaign = response.data.subjectToCampaign;
                    this.warranty.subjectToCampaignNotes = response.data.subjectToCampaignNotes;
                    this.customWarranties = response.data.customWarrantyDates;
                } else {
                    this.warranty.bumperToBumperWarrantyStart = null;
                    this.warranty.bumperToBumperWarrantyEnd = null;
                    this.warranty.engineWarrantyStart = null;
                    this.warranty.engineWarrantyEnd = null;
                    this.warranty.transmissionWarrantyStart = null;
                    this.warranty.transmissionWarrantyEnd = null;
                    this.warranty.bodyWarrantyStart = null;
                    this.warranty.bodyWarrantyEnd = null;
                    this.customWarranties = [];
                }
            } catch (error) {
                this.$toast.error('There was an error checking warranty status for the selected unit');
            }
        },
        selectedSource() {
            this.value.sourceId = this.selectedSource.id;
            this.value.source = this.selectedSource;
        },
        async search(val) {
            this.performSearch(val, this);
        }
    }
};
</script>
